/**
 * App Store - глобальное состояние приложения
 * Управляет активным контекстом: агентом, символом, таймфреймом, темой
 */

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

import i18n from '../../i18n/config';
import { normalizeAppLocale, type AppLocale } from '../../i18n/locale';
import { normalizeIntervalId } from '../config/constants';

interface AppState {
  // Активный контекст
  activeAgentId: string | null;
  activeSymbol: string;
  activeTimeframe: string;
  theme: 'dark' | 'light';
  locale: AppLocale;

  // UI состояние
  sidebarCollapsed: boolean;
  rightPanelCollapsed: boolean;

  // Настройки
  autoRefresh: boolean;
  refreshInterval: number;

  // Actions
  setActiveAgent: (agentId: string | null) => void;
  setActiveSymbol: (symbol: string) => void;
  setActiveTimeframe: (timeframe: string) => void;
  setTheme: (theme: 'dark' | 'light') => void;
  setLocale: (locale: AppLocale) => void;
  toggleSidebar: () => void;
  toggleRightPanel: () => void;
  setAutoRefresh: (enabled: boolean) => void;
  setRefreshInterval: (interval: number) => void;

  // Selectors
  getActiveContext: () => {
    agentId: string | null;
    symbol: string;
    timeframe: string;
  };
}

function applyLocale(locale: AppLocale) {
  const nextLocale = normalizeAppLocale(locale);
  document.documentElement.lang = nextLocale;
  void i18n.changeLanguage(nextLocale);
}

// Создаем store с persist middleware для сохранения состояния
export const useAppStore = create<AppState>()(
  persist(
    (set, get) => ({
      // Initial state
      activeAgentId: null,
      activeSymbol: 'BTCUSDT',
      activeTimeframe: 'T:1m',
      theme: 'dark',
      locale: 'en',
      sidebarCollapsed: false,
      rightPanelCollapsed: false,
      autoRefresh: true,
      refreshInterval: 5000,

      // Actions
      setActiveAgent: (agentId) => {
        set({ activeAgentId: agentId });
      },

      setActiveSymbol: (symbol) => {
        set({ activeSymbol: symbol });
      },

      setActiveTimeframe: (timeframe) => {
        set({ activeTimeframe: normalizeIntervalId(timeframe) });
      },

      setTheme: (theme) => {
        set({ theme });
        // Apply theme to document
        document.documentElement.classList.toggle('dark', theme === 'dark');
      },

      setLocale: (locale) => {
        const nextLocale = normalizeAppLocale(locale);
        set({ locale: nextLocale });
        applyLocale(nextLocale);
      },

      toggleSidebar: () => {
        set((state) => ({ sidebarCollapsed: !state.sidebarCollapsed }));
      },

      toggleRightPanel: () => {
        set((state) => ({ rightPanelCollapsed: !state.rightPanelCollapsed }));
      },

      setAutoRefresh: (enabled) => {
        set({ autoRefresh: enabled });
      },

      setRefreshInterval: (interval) => {
        set({ refreshInterval: interval });
      },

      // Selectors
      getActiveContext: () => {
        const state = get();
        return {
          agentId: state.activeAgentId,
          symbol: state.activeSymbol,
          timeframe: normalizeIntervalId(state.activeTimeframe),
        };
      },
    }),
    {
      name: 'algosys-app-state',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        activeAgentId: state.activeAgentId,
        activeSymbol: state.activeSymbol,
        activeTimeframe: state.activeTimeframe,
        theme: state.theme,
        locale: state.locale,
        sidebarCollapsed: state.sidebarCollapsed,
        rightPanelCollapsed: state.rightPanelCollapsed,
        autoRefresh: state.autoRefresh,
        refreshInterval: state.refreshInterval,
      }),
      onRehydrateStorage: () => (state) => {
        if (state?.locale) {
          applyLocale(state.locale);
        }
      },
    }
  )
);

// Initialize theme on app start
if (typeof window !== 'undefined') {
  const theme = useAppStore.getState().theme;
  document.documentElement.classList.toggle('dark', theme === 'dark');
  applyLocale(useAppStore.getState().locale);
}

// Export store instance for advanced usage
export const appStore = useAppStore;
